import React, { useCallback, useEffect, useRef, useState } from "react";
import styled from "styled-components";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";

const Wrapper = styled.div`
  margin: 0 auto;
  position: relative;
`;

const Grid = styled.div`
  display: flex;
  align-items: stretch;
  gap: 18px;

  overflow-x: auto;
  padding: 2px 56px 8px;
  scroll-snap-type: x mandatory;
  -webkit-overflow-scrolling: touch;

  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }

  @media screen and (max-width: 860px) {
    flex-direction: column;
    overflow-x: visible;
    padding: 0;
    scroll-snap-type: none;
  }
`;

const EdgeFade = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  width: 72px;
  pointer-events: none;
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  transition: opacity 180ms ease;
  z-index: 6;

  @media screen and (max-width: 860px) {
    display: none;
  }
`;

const LeftFade = styled(EdgeFade)`
  left: 0;
  background: linear-gradient(90deg, rgba(11, 12, 18, 1), transparent);
`;

const RightFade = styled(EdgeFade)`
  right: 0;
  background: linear-gradient(270deg, rgba(11, 12, 18, 1), transparent);
`;

const NavButton = styled.button`
  appearance: none;
  position: absolute;
  top: 50%;
  transform: translateY(-50%);

  width: 44px;
  height: 44px;
  border-radius: 999px;
  border: 1px solid rgba(255, 255, 255, 0.14);
  background: rgba(11, 12, 18, 0.72);
  color: rgba(255, 255, 255, 0.88);
  backdrop-filter: blur(12px);

  display: inline-flex;
  align-items: center;
  justify-content: center;

  cursor: pointer;
  box-shadow: 0 16px 46px rgba(0, 0, 0, 0.45);

  opacity: ${({ $hidden }) => ($hidden ? 0 : 1)};
  pointer-events: ${({ $hidden }) => ($hidden ? "none" : "auto")};
  transition: opacity 180ms ease, transform 180ms ease, background 180ms ease,
    border-color 180ms ease;
  z-index: 7;

  &:hover {
    transform: translateY(-50%) scale(1.02);
    background: rgba(11, 12, 18, 0.82);
    border-color: rgba(255, 255, 255, 0.22);
  }

  &:active {
    transform: translateY(-50%) scale(1);
  }

  @media screen and (max-width: 860px) {
    display: none;
  }
`;

const PrevButton = styled(NavButton)`
  left: 10px;
`;

const NextButton = styled(NavButton)`
  right: 10px;
`;

const Step = styled.div`
  position: relative;
  border: 1px solid var(--border-0);
  background: var(--surface-0);
  border-radius: var(--radius-lg);
  padding: 18px 18px 20px;
  overflow: hidden;
  height: 100%;
  width: 100%;

  box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.18) inset;
  transition: background 180ms ease, border-color 180ms ease;

  &::before {
    content: "";
    position: absolute;
    inset: 0;
    background: radial-gradient(
        420px circle at 20% 0%,
        rgba(124, 92, 255, 0.18),
        transparent 55%
      ),
      radial-gradient(
        420px circle at 85% 15%,
        rgba(185, 255, 102, 0.10),
        transparent 55%
      );
    opacity: 0.45;
    pointer-events: none;
  }
`;

const StepSlot = styled.div`
  position: relative;
  flex: 0 0 clamp(260px, 34vw, 340px);
  display: flex;
  align-items: stretch;
  scroll-snap-align: start;

  transition: transform 180ms ease;

  &:hover {
    transform: translateY(-2px);
  }

  &:hover ${Step} {
    background: var(--surface-1);
    border-color: rgba(255, 255, 255, 0.18);
  }

  &:not(:last-child)::after {
    content: "➜";
    position: absolute;
    top: 50%;
    right: -13px;
    transform: translate(50%, -50%);

    width: 34px;
    height: 34px;
    border-radius: 999px;
    border: 1px solid rgba(185, 255, 102, 0.35);
    background: rgba(185, 255, 102, 0.10);

    display: flex;
    align-items: center;
    justify-content: center;

    color: var(--accent-0);
    font-weight: 900;
    font-size: 16px;
    text-shadow: 0 10px 26px rgba(185, 255, 102, 0.14);

    z-index: 5;
    pointer-events: none;
  }

  @media screen and (max-width: 860px) {
    flex: 1 1 auto;
    scroll-snap-align: unset;

    &:not(:last-child)::after {
      top: auto;
      right: 50%;
      bottom: -13px;
      transform: translate(50%, 50%) rotate(90deg);
    }
  }
`;

const StepInner = styled.div`
  position: relative;
  display: grid;
  gap: 12px;
`;

const StepHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const StepNumber = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 34px;
  height: 34px;
  padding: 0 10px;
  border-radius: 999px;

  border: 1px solid rgba(185, 255, 102, 0.38);
  background: rgba(185, 255, 102, 0.10);
  color: rgba(255, 255, 255, 0.88);

  font-weight: 850;
  letter-spacing: 0.02em;
  font-size: 12px;
`;

const StepTitle = styled.h4`
  color: var(--text-0);
  font-size: 17px;
  line-height: 1.2;
`;

const StepText = styled.p`
  font-size: 15px;
`;

export const WorkFlow = () => {
  const scrollerRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateScrollState = useCallback(() => {
    const scroller = scrollerRef.current;
    if (!scroller) return;

    const maxScrollLeft = scroller.scrollWidth - scroller.clientWidth;
    setCanScrollLeft(scroller.scrollLeft > 2);
    setCanScrollRight(scroller.scrollLeft < maxScrollLeft - 2);
  }, []);

  useEffect(() => {
    updateScrollState();
    const scroller = scrollerRef.current;
    if (!scroller) return;

    const onScroll = () => updateScrollState();
    scroller.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", updateScrollState);

    return () => {
      scroller.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", updateScrollState);
    };
  }, [updateScrollState]);

  const scrollByPage = useCallback((direction) => {
    const scroller = scrollerRef.current;
    if (!scroller) return;

    const amount = Math.max(280, Math.round(scroller.clientWidth * 0.82));
    scroller.scrollBy({ left: direction * amount, behavior: "smooth" });
  }, []);

  return (
    <Wrapper>
      <LeftFade $visible={canScrollLeft} />
      <RightFade $visible={canScrollRight} />
      <PrevButton
        type="button"
        aria-label="Previous steps"
        onClick={() => scrollByPage(-1)}
        $hidden={!canScrollLeft}
      >
        <ArrowBackRoundedIcon />
      </PrevButton>
      <NextButton
        type="button"
        aria-label="Next steps"
        onClick={() => scrollByPage(1)}
        $hidden={!canScrollRight}
      >
        <ArrowForwardRoundedIcon />
      </NextButton>

      <Grid ref={scrollerRef} aria-label="How we get it done steps">
        <StepSlot>
          <Step>
            <StepInner>
              <StepHeader>
                <StepNumber>0</StepNumber>
                <StepTitle>Our Working Style</StepTitle>
              </StepHeader>
              <StepText>
                We work on a project-to-project basis and we do not provide
                services unless the work being done is agreed upon by us and our
                clients in writing. We hold this standard for projects we
                develop and any support we provide to existing development teams
                in an organization.
              </StepText>
            </StepInner>
          </Step>
        </StepSlot>

        <StepSlot>
          <Step>
            <StepInner>
              <StepHeader>
                <StepNumber>1</StepNumber>
                <StepTitle>First Point of Contact</StepTitle>
              </StepHeader>
              <StepText>
                We'll have you fill out a questionnaire (will take 10-15
                minutess) in order to understand what your current needs are,
                and gauge whether or not we're a good fit to help you reach your
                goal.
              </StepText>
            </StepInner>
          </Step>
        </StepSlot>

        <StepSlot>
          <Step>
            <StepInner>
              <StepHeader>
                <StepNumber>2</StepNumber>
                <StepTitle>Requirements Analysis</StepTitle>
              </StepHeader>
              <StepText>
                If we're a good match we will meet to discuss the higher level
                technical requirements for your project. Then we outline them
                down to the smallest individual task with hourly estimates to
                complete each task and provide you with a quotation for the cost
                of completing them all, and a projected date of completion.
              </StepText>
            </StepInner>
          </Step>
        </StepSlot>

        <StepSlot>
          <Step>
            <StepInner>
              <StepHeader>
                <StepNumber>3</StepNumber>
                <StepTitle>Development</StepTitle>
              </StepHeader>
              <StepText>
                Once an agreement has been reached, we will begin development of
                the project using the development tools and methodologies we
                specialize in. Periodically we will conduct demos of the
                project in development bi-weekly or at your discretion.
              </StepText>
            </StepInner>
          </Step>
        </StepSlot>

        <StepSlot>
          <Step>
            <StepInner>
              <StepHeader>
                <StepNumber>4</StepNumber>
                <StepTitle>Testing</StepTitle>
              </StepHeader>
              <StepText>
                At Auxiliary One we take testing very seriously. We use cutting
                edge CI/CD tools like Gitlab and Circle to make sure that all
                layers of applications we develop are tested thoroughly using a
                test-driven-development approach before being sent into
                production environments.
              </StepText>
            </StepInner>
          </Step>
        </StepSlot>

        <StepSlot>
          <Step>
            <StepInner>
              <StepHeader>
                <StepNumber>5</StepNumber>
                <StepTitle>Deployment</StepTitle>
              </StepHeader>
              <StepText>
                For Web and Cloud based solutions we typically allow our clients
                to host services we create on their own hosting platforms. We do
                offer services such as Maintenance Contracts, where we host and
                monitor applications on behalf of our clients at a monthly rate.
              </StepText>
            </StepInner>
          </Step>
        </StepSlot>
      </Grid>
    </Wrapper>
  );
};
